import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import Table_theme from "../Themes/TableTheme";
import FormThemes from "../Themes/FormThemes";
import {
  formatDate,
  formatDateLocal,
  formatDateLocal_TF,
} from "../utils/dateUtils"; // adjust path
import {
  Box,
  MenuItem,
  Pagination,
  Typography,
  Grid,
  TextField,
  Button,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Snackbar,
  Alert,
  TableContainer,
  CssBaseline,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import debounce from "lodash.debounce";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import HistoryIcon from "@mui/icons-material/History";
import Tooltip from "@mui/material/Tooltip";
import { formatDateTimeUS } from "../utils/dateUtils";
import { da } from "date-fns/locale";
import { useCurrentUser } from "../utils/useCurrentUser";

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Security configuration
const SECURITY_CONFIG = {
  TIME_MANIPULATION_THRESHOLD: 5000, // 5 seconds - adjust as needed
  LOGOUT_DELAY: 5000, // 5 seconds delay to show warning message
  ENABLE_TIME_PROTECTION: true, // Can be disabled for testing
};

// Always convert any date to IST
const toIST = (date = new Date()) => {
  return new Date(date.toLocaleString("en-US", { timeZone: "Asia/Kolkata" }));
};

const getISTDateString = (date = new Date()) => {
  const d = toIST(date);

  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

// IST datetime formatter
const getISTDateTimeString = (date = new Date()) => {
  const d = toIST(date);

  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");

  const hour = String(d.getHours()).padStart(2, "0");
  const min = String(d.getMinutes()).padStart(2, "0");

  return `${year}-${month}-${day} ${hour}:${min}:00`;
};

// Parse "YYYY-MM-DD HH:MM:SS" into Date object in local time
const getLocalDateString = (dateObj = new Date()) => {
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, "0");
  const day = String(dateObj.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getISTDateObj = () => {
  const istString = new Date().toLocaleString("en-US", {
    timeZone: "Asia/Kolkata",
  });
  return new Date(istString);
};

const getTodayDate = () => {
  const d = getISTDateObj();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getYesterdayDate = () => {
  const d = getISTDateObj();
  d.setDate(d.getDate() - 1);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};
const formatToMMDDYYYY = (dateStr) => {
  if (!dateStr) return "";
  const [year, month, day] = dateStr.split("-");
  return `${month}/${day}/${year}`;
};

const EmployeeAttendance = () => {
  const { userId, empId, boId, roles, hasPermission } = useCurrentUser();
  const navigate = useNavigate();
  const [isCheckedIn, setIsCheckedIn] = useState(false);
  const [dateError, setDateError] = useState(""); // To show inline error
  const [todayHistory, setTodayHistory] = useState([]);
  const [empName, setEmpName] = useState({});
  const [isStatusLoading, setIsStatusLoading] = useState(true);
  const [validationError, setValidationError] = useState("");
  const [actionType, setActionType] = useState("checkin");
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyData, setHistoryData] = useState([]);
  const [searchEmpName, setSearchEmpName] = useState("");
  const [comments, setComments] = useState("");
  const [statusMsg, setStatusMsg] = useState("");
  const [checkInDisabled, setCheckInDisabled] = useState(false);
  const [checkOutDisabled, setCheckOutDisabled] = useState(true);
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [checkInTime, setCheckInTime] = useState({
    hour: "",
    min: "",
    meridian: "AM",
  });
  const [checkOutTime, setCheckOutTime] = useState({
    hour: "",
    min: "",
    meridian: "PM",
  });
  //const [date, setDate] = useState(() => getLocalDateString());
  // const [date, setDate] = useState(() => getISTDateString());
  const [displayDate, setDisplayDate] = useState(
    formatToMMDDYYYY(getISTDateString())
  );
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [searchDate, setSearchDate] = useState(getTodayDate());
  const [checkOutDate, setCheckOutDate] = useState(getISTDateString());
  const [pendingRecord, setPendingRecord] = useState(null); // Track pending check-in record
  const [datesCrossMidnight, setDatesCrossMidnight] = useState(false); // Track if checkout crosses midnight
  const [currentDateTime, setCurrentDateTime] = useState(new Date()); // Real-time date/time
  const [lastKnownTime, setLastKnownTime] = useState(Date.now()); // For detecting time manipulation
  const userRole = Array.isArray(roles) ? roles[0] : roles;

  // Logout function for security and normal logout
  const handleLogout = async () => {
    try {
      // IMPORTANT: Wait for logout API to complete (clears HttpOnly cookie)
      await fetch(`${BASE_URL}/api/logout`, {
        method: "POST",
        credentials: "include", // Important for CORS with cookies
      });

      console.log("Logout API completed successfully");
    } catch (error) {
      console.error("Logout API failed:", error);
      // Continue with logout even if API fails
    }

    // Clear storage
    try {
      localStorage.clear();
      sessionStorage.clear();
    } catch (error) {
      console.error("Storage clear error:", error);
    }

    // Navigate after API call completes
    navigate("/login", { replace: true });
  };

  useEffect(() => {
    if (!empId || !boId) return;

    const fetchLoginUser = async () => {
      try {
        const res = await fetch(`${BASE_URL}/api/checkin/${empId}/${boId}`);
        if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);

        const data = await res.json();
        if (data.length > 0) {
          setEmpName(data[0]); // store first object instead of whole array
        }

        // IMMEDIATE CHECK: Look for pending checkouts on page load
        console.log("PAGE LOAD: Checking for pending checkouts...");
        await fetchTodayHistory(page, empId, boId);
        await fetchLastStatus();

        // Additional immediate validation
        setTimeout(async () => {
          console.log("DELAYED CHECK: Re-validating pending status...");
          const immediateCheck = await getLastPendingCheckIn(empId);
          console.log("DELAYED CHECK RESULT:", immediateCheck);

          if (immediateCheck?.pending) {
            const pendingDate = immediateCheck.attendance_date;
            const today = getTodayDate();

            if (pendingDate !== today) {
              console.log(
                "DELAYED CHECK: Found pending checkout, forcing UI update"
              );
              setCheckInDisabled(true);
              setCheckOutDisabled(false);
              setIsCheckedIn(true);
              setActionType("checkout");

              const formattedDate = formatToMMDDYYYY(pendingDate);
              setStatusMsg(
                `⚠️ CRITICAL: Pending checkout from ${formattedDate} detected. Complete checkout to enable new check-ins.`
              );
              setMessage(
                `System detected pending checkout from ${formattedDate}. Please complete that checkout first.`
              );
              setOpen(true);
            }
          }
        }, 2000); // Check again after 2 seconds
      } catch (err) {
        console.error("Failed to fetch employee details", err);
      }
    };
    fetchLoginUser();
  }, [empId, boId, page]);

  useEffect(() => {
    fetchLastStatus();
    fetchTodayHistory(page, empId, boId);
  }, [page, limit, empId, boId]);

  // Real-time date/time updates with time manipulation detection
  useEffect(() => {
    const timer = setInterval(() => {
      const now = new Date();
      const currentTime = now.getTime();
      const expectedTime = lastKnownTime + 1000; // Expected time (1 second later)
      const timeDifference = Math.abs(currentTime - expectedTime);

      // Detect significant time jumps (configurable threshold)
      // This indicates system time was manually changed
      if (
        SECURITY_CONFIG.ENABLE_TIME_PROTECTION &&
        lastKnownTime > 0 &&
        timeDifference > SECURITY_CONFIG.TIME_MANIPULATION_THRESHOLD
      ) {
        console.warn("System time manipulation detected!", {
          lastKnownTime: new Date(lastKnownTime).toISOString(),
          currentTime: new Date(currentTime).toISOString(),
          difference: timeDifference / 1000 + " seconds",
        });

        // Force logout due to time manipulation
        handleTimeManipulationDetected(timeDifference);
        return;
      }

      const previousDate = currentDateTime.toDateString();
      const newDate = now.toDateString();

      setCurrentDateTime(now);
      setLastKnownTime(currentTime);

      // Check if day has changed naturally (midnight crossed)
      if (
        previousDate !== newDate &&
        currentDateTime.getTime() > 0 &&
        timeDifference <= SECURITY_CONFIG.TIME_MANIPULATION_THRESHOLD
      ) {
        console.log(
          "Natural day change detected! Updating checkout date and refreshing data..."
        );

        // Update checkout date to new day if user is checked in
        if (isCheckedIn) {
          const newDateISO = getISTDateString(now);
          setCheckOutDate(newDateISO);
          setDatesCrossMidnight(true);

          // Show notification about day change
          setMessage(
            `Day changed to ${formatToMMDDYYYY(
              newDateISO
            )}. Checkout date updated automatically.`
          );
          setOpen(true);
        }

        // Refresh data with new frontend date
        console.log("Refreshing data for new day:", getISTDateString(now));
        fetchTodayHistory(page, empId, boId);
        fetchLastStatus();
      }
    }, 1000); // Update every second

    return () => clearInterval(timer);
  }, [currentDateTime, isCheckedIn, page, empId, boId, lastKnownTime]);

  // Handle time manipulation detection
  const handleTimeManipulationDetected = (timeDifference) => {
    const minutes = Math.round(timeDifference / 1000 / 60);
    const direction = timeDifference > 0 ? "forward" : "backward";

    // Clear all sensitive data
    setIsCheckedIn(false);
    setCheckInDisabled(false);
    setCheckOutDisabled(true);
    setPendingRecord(null);
    setTodayHistory([]);

    // Show security warning
    setMessage(
      `Security Alert: System time was changed ${direction} by ${minutes} minutes. ` +
        `For security reasons, you have been logged out. Redirecting to login...`
    );
    setOpen(true);

    // Force logout after a short delay to show the message
    setTimeout(() => {
      // Clear all local storage/session data
      localStorage.clear();
      sessionStorage.clear();

      // Use the proper logout function
      handleLogout();
    }, SECURITY_CONFIG.LOGOUT_DELAY);
  };

  // set rows per page
  const rowsPerPage = 10;

  // Real-time current date and time in IST
  const currentDate = currentDateTime.toLocaleDateString("en-US", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });

  const currentTime = currentDateTime.toLocaleTimeString("en-US", {
    timeZone: "Asia/Kolkata",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });
  // handle history dialog close
  const handleHistoryClose = () => {
    setSearchEmpName(""); // reset employee search
    setSearchDate(getTodayDate()); // optional: reset date filter
    setHistoryData([]); // optional: clear table
    setHistoryOpen(false); // close dialog
  };
  const hours = Array.from({ length: 12 }, (_, i) =>
    (i + 1).toString().padStart(2, "0")
  );
  // const minutes = ["00", "15", "30", "45"];
  // full minutes list
  const minutes = Array.from({ length: 60 }, (_, i) =>
    i.toString().padStart(2, "0")
  );
  // AM/PM options
  const ampm = ["AM", "PM"];

  // Update current time every second
  function parseLocalDateTime(dateTimeStr) {
    const [datePart, timePart] = dateTimeStr.split(" ");
    const [year, month, day] = datePart.split("-").map(Number);
    const [hour, min, sec = 0] = timePart.split(":").map(Number);
    return new Date(year, month - 1, day, hour, min, sec); // local time
  }
  // Get today's date in MM/DD/YYYY format for comparison
  const dateIST = new Date().toLocaleString("en-US", {
    timeZone: "Asia/Kolkata",
  });

  const d = new Date(dateIST);

  const formattedCurrentDate =
    `${String(d.getMonth() + 1).padStart(2, "0")}/` +
    `${String(d.getDate()).padStart(2, "0")}/` +
    `${d.getFullYear()}`;

  console.log(formattedCurrentDate);

  const createISTDateFromString = (str) => {
    console.log("str-->", str);
    const [datePart, timePart] = str.split(" ");
    const [y, m, d] = datePart.split("-").map(Number);
    const [hh, mm, ss] = timePart.split(":").map(Number);

    // Just create Date in local time (no UTC shift)
    return new Date(y, m - 1, d, hh, mm, ss);
  };

  // Create an IST date from Y-M-D and H:M in IST
  const createISTDate = (y, m, d, hh, mm) => {
    // Convert IST → UTC by subtracting 5:30
    return new Date(Date.UTC(y, m - 1, d, hh - 5, mm - 30, 0));
  };
  const getISTTimestamp = (dateStr, timeObj, dateOnly = false) => {
    const [y, m, d] = dateStr.split("-").map(Number);

    let hh, mm;

    if (!timeObj) {
      const nowIST = toIST();
      hh = nowIST.getHours();
      mm = nowIST.getMinutes();
    } else {
      hh = parseInt(timeObj.hour, 10);
      mm = parseInt(timeObj.min || 0, 10);

      if (timeObj.meridian === "PM" && hh !== 12) hh += 12;
      if (timeObj.meridian === "AM" && hh === 12) hh = 0;
    }

    const finalDate = createISTDate(y, m, d, hh, mm);

    if (dateOnly) return getISTDateString(finalDate);

    return getISTDateTimeString(finalDate);
  };

  const handleCheckIn = async () => {
    setValidationError("");

    console.log("Starting check-in process...");

    // IMMEDIATE HARD-STOP: Check database directly for any pending checkouts
    try {
      console.log("HARD-STOP CHECK: Verifying no pending checkouts...");

      const directCheckResponse = await fetch(
        `${BASE_URL}/api/checkin/today-history?empId=${empId}&boId=${boId}&currentDate=${getISTDateString(
          currentDateTime
        )}`,
        {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        }
      );

      if (directCheckResponse.ok) {
        const historyData = await directCheckResponse.json();
        console.log("Direct history check:", historyData);

        // Check if any record has checkin_time but no checkout_time
        const pendingRecords =
          historyData.data?.filter((record) => {
            const hasCheckin =
              record.checkin_time && record.checkin_time !== "—";
            const hasCheckout =
              record.checkout_time && record.checkout_time !== "—";
            return hasCheckin && !hasCheckout;
          }) || [];

        console.log("Found pending records:", pendingRecords);

        if (pendingRecords.length > 0) {
          const latestPending = pendingRecords[0];
          const pendingDate = latestPending.attendance_date;
          const today = getTodayDate();
          const todayFormatted = formatToMMDDYYYY(today);

          console.log("HARD-STOP: Pending checkout detected!", {
            pendingDate,
            today: todayFormatted,
            latestPending,
          });

          // If pending date is not today, block check-in
          if (pendingDate !== todayFormatted) {
            const errorMessage = `SYSTEM BLOCK: You have a pending checkout from ${pendingDate}. Complete that checkout before checking in again.`;

            setValidationError(errorMessage);
            setMessage(errorMessage);
            setOpen(true);

            // Force checkout mode
            setCheckInDisabled(true);
            setCheckOutDisabled(false);
            setIsCheckedIn(true);
            setActionType("checkout");

            console.log("CHECK-IN BLOCKED BY HARD-STOP");
            return; // BLOCK CHECK-IN
          }
        }
      }
    } catch (directCheckError) {
      console.error("Direct check failed:", directCheckError);
      // Continue with original validation if direct check fails
    }

    // ORIGINAL VALIDATION (as backup)
    const pendingRecord = await getLastPendingCheckIn(empId);

    console.log("Pending record check result:", pendingRecord);

    if (pendingRecord?.pending) {
      const pendingDate = pendingRecord.attendance_date;
      const today = getTodayDate();

      console.log("Pending checkout detected:", {
        pendingDate,
        today,
        isPendingFromPreviousDay: pendingDate !== today,
      });

      if (pendingDate !== today) {
        // There's a pending checkout from a previous day
        const formattedPendingDate = formatToMMDDYYYY(pendingDate);
        const errorMessage = `You have a pending checkout from ${formattedPendingDate}. Please complete that checkout before checking in again.`;

        console.log("BLOCKING CHECK-IN:", errorMessage);

        setValidationError(errorMessage);
        setMessage(
          `Pending checkout detected for ${formattedPendingDate}. Please check out for that day first.`
        );
        setOpen(true);

        // Force the UI to show checkout mode for the pending record
        setCheckInDisabled(true);
        setCheckOutDisabled(false);
        setIsCheckedIn(true);
        setActionType("checkout");

        return; // Prevent check-in
      }
    }

    // Additional safety check - verify no records exist without checkout for this employee
    try {
      const verificationResponse = await fetch(
        `${BASE_URL}/api/checkin/verify-no-pending/${empId}/${boId}`,
        {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        }
      );

      if (verificationResponse.ok) {
        const verificationData = await verificationResponse.json();
        if (verificationData.hasPending) {
          console.log(
            "VERIFICATION FAILED: Found pending checkout:",
            verificationData
          );
          setValidationError(
            "System detected a pending checkout. Please refresh the page and complete your previous checkout first."
          );
          return;
        }
      }
    } catch (verificationError) {
      console.warn(
        "Verification check failed (continuing):",
        verificationError
      );
      // Continue with check-in if verification endpoint doesn't exist yet
    }

    console.log("All validations passed, proceeding with check-in...");

    const name = empName.employee_name;

    // Use the selected date from displayDate (convert MM/DD/YYYY to YYYY-MM-DD)
    let attendance_date;
    if (displayDate && displayDate.includes("/")) {
      const [month, day, year] = displayDate.split("/");
      attendance_date = `${year}-${month.padStart(2, "0")}-${day.padStart(
        2,
        "0"
      )}`;
    } else {
      attendance_date = getISTDateString(); // fallback to today
    }

    // If user hasn't selected time, default to current IST time
    let checkinTimeToUse = checkInTime;
    if (!checkInTime.hour || !checkInTime.min || !checkInTime.meridian) {
      const nowIST = toIST(); // your helper that returns current IST as Date
      let hours = nowIST.getHours();
      const minutes = nowIST.getMinutes();
      const meridian = hours >= 12 ? "PM" : "AM";
      hours = hours % 12 || 12; // convert 24h -> 12h

      checkinTimeToUse = {
        hour: hours.toString().padStart(2, "0"),
        min: minutes.toString().padStart(2, "0"),
        meridian,
      };

      setCheckInTime(checkinTimeToUse); // optional, update UI
    }

    // Always get IST datetime
    const checkin_time = getISTTimestamp(attendance_date, checkinTimeToUse);

    // Validation
    const checkinDateObj = createISTDateFromString(checkin_time);
    const nowIST = toIST();

    // Validate check-in date is not in the future
    if (checkinDateObj > nowIST) {
      setValidationError("Check-in time cannot be in the future.");
      return;
    }

    // Validate check-in date is not more than 1 day old (except for missed checkouts)
    const oneDayAgo = new Date(nowIST);
    oneDayAgo.setDate(oneDayAgo.getDate() - 1);
    oneDayAgo.setHours(0, 0, 0, 0); // Start of yesterday

    if (checkinDateObj < oneDayAgo) {
      setValidationError(
        "Check-in date cannot be more than 1 day old. Please contact administrator for older entries."
      );
      return;
    }

    try {
      const response = await fetch(`${BASE_URL}/api/checkin/in`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          boId,
          empId,
          name,
          attendance_date,
          checkin_time,
          checkin_comments: comments,
        }),
      });

      const data = await response.json();
      const success = response.ok; // treat any 2xx as success

      if (success) {
        setIsCheckedIn(true);
        setComments("");
        setCheckInDisabled(true);
        setCheckOutDisabled(false);
        setActionType("checkout");
      } else {
        setIsCheckedIn(false);
        setCheckInDisabled(false);
        setCheckOutDisabled(true);
      }

      const fallback = success ? "Check-in successful" : "Check-in failed";
      setMessage((data && data.message) || fallback);
      setOpen(true);

      // Refresh data and status and wait for them so UI reflects new state
      try {
        await fetchTodayHistory(page, empId, boId);
        await fetchLastStatus();
      } catch (refreshErr) {
        console.error("Error refreshing after check-in:", refreshErr);
      }
    } catch (error) {
      console.error("Check-in error:", error);
      setMessage("Check-in error");
      setOpen(true);
      setIsCheckedIn(true);
      fetchTodayHistory(page, empId, boId);
    }
  };

  // const handleCheckOut = async () => {
  //   setValidationError("");
  //   const name = empName.employee_name;
  //   const checkout_comments = comments;
  //   const record = await getLastPendingCheckIn(empId);
  //   console.log("record-->", record);

  //   if (!record?.pending) {
  //     setMessage("No pending check-in found for checkout.");
  //     setOpen(true);
  //     return;
  //   }

  //   // If user hasn't selected time, default to current IST time
  //   let checkoutTimeToUse = checkOutTime;
  //   if (!checkOutTime.hour || !checkOutTime.min || !checkOutTime.meridian) {
  //     const nowIST = toIST(); // your helper that returns current IST as Date
  //     console.log("nowIST:", nowIST);
  //     let hours = nowIST.getHours();
  //     const minutes = nowIST.getMinutes();
  //     const meridian = hours >= 12 ? "PM" : "AM";
  //     hours = hours % 12 || 12; // convert 24h -> 12h

  //     checkoutTimeToUse = {
  //       hour: hours.toString().padStart(2, "0"),
  //       min: minutes.toString().padStart(2, "0"),
  //       meridian,
  //     };
  //     console.log("checkoutTimeToUse:", checkoutTimeToUse);
  //     setCheckOutTime(checkoutTimeToUse); // optional, update UI
  //   }

  //   const { attendance_id, attendance_date, checkin_time } = record;

  //   // ENHANCED LOGIC: Handle midnight crossing
  //   let finalCheckoutDate = checkOutDate;

  //   // Parse check-in time to determine if we need to adjust checkout date
  //   const checkinDateObj = createISTDateFromString(checkin_time);

  //   // Create initial checkout datetime with selected date
  //   let checkout_time = getISTTimestamp(finalCheckoutDate, checkoutTimeToUse);
  //   let checkoutDateObj = createISTDateFromString(checkout_time);

  //   // If checkout is before checkin, it likely means we crossed midnight
  //   if (checkoutDateObj < checkinDateObj) {
  //     // Check if this is a reasonable midnight crossing (within 24 hours)
  //     const nextDay = new Date(checkinDateObj);
  //     nextDay.setDate(nextDay.getDate() + 1);

  //     // Convert checkout time to proper 24-hour format for next day
  //     let checkoutHour24 = parseInt(checkoutTimeToUse.hour);
  //     if (checkoutTimeToUse.meridian === "PM" && checkoutHour24 !== 12) {
  //       checkoutHour24 += 12;
  //     } else if (checkoutTimeToUse.meridian === "AM" && checkoutHour24 === 12) {
  //       checkoutHour24 = 0;
  //     }

  //     nextDay.setHours(checkoutHour24, parseInt(checkoutTimeToUse.min), 0, 0);

  //     // If the time makes sense for next day, use next day
  //     if (
  //       nextDay > checkinDateObj &&
  //       nextDay.getTime() - checkinDateObj.getTime() <= 24 * 60 * 60 * 1000
  //     ) {
  //       const nextDayStr = getISTDateString(nextDay);
  //       finalCheckoutDate = nextDayStr;
  //       checkout_time = getISTTimestamp(finalCheckoutDate, checkoutTimeToUse);
  //       checkoutDateObj = createISTDateFromString(checkout_time);

  //       // Update the UI to show the corrected date
  //       setCheckOutDate(finalCheckoutDate);
  //       setDatesCrossMidnight(true); // Flag that we crossed midnight

  //       // Show user a message about the date correction
  //       console.log(
  //         `Checkout date automatically adjusted to ${formatToMMDDYYYY(
  //           finalCheckoutDate
  //         )} due to midnight crossing.`
  //       );
  //     }
  //   }

  //   const nowIST = toIST();

  //   // Validate checkout order (after potential date correction)
  //   if (checkoutDateObj < checkinDateObj) {
  //     setValidationError(
  //       "Check-out time cannot be earlier than check-in time. If you worked past midnight, please select the next day's date."
  //     );
  //     return;
  //   }

  //   // Validate against current time
  //   if (checkoutDateObj > nowIST) {
  //     setValidationError("Check-out time cannot be in the future.");
  //     return;
  //   }

  //   // Validate reasonable working hours (prevent extremely long shifts)
  //   const workingHours =
  //     (checkoutDateObj.getTime() - checkinDateObj.getTime()) / (1000 * 60 * 60);
  //   if (workingHours > 24) {
  //     setValidationError(
  //       "Working hours cannot exceed 24 hours. Please check your dates and times."
  //     );
  //     return;
  //   }

  //   const type = "out";
  //   try {
  //     const response = await fetch(`${BASE_URL}/api/checkin/${type}`, {
  //       method: "POST",
  //       headers: { "Content-Type": "application/json" },
  //       body: JSON.stringify({
  //         empId,
  //         boId,
  //         name,
  //         attendance_id,
  //         attendance_date,
  //         checkout_time,
  //         checkout_comments,
  //       }),
  //     });

  //     if (response.status === 200) {
  //       setIsCheckedIn(false); // Now this will work properly
  //       setComments("");
  //     }

  //     const data = await response.json();

  //     setMessage(data.message);
  //     setOpen(true);
  //     await fetchTodayHistory(page, empId, boId);
  //     await fetchLastStatus();
  //   } catch (error) {
  //     console.error("Check-out error:", error);
  //     setMessage("Check-out error");
  //     setOpen(true);
  //     //  setIsCheckedIn(false);
  //     await fetchTodayHistory(page, empId, boId);
  //     await fetchLastStatus();
  //   }
  // };

  const handleCheckOut = async () => {
    setValidationError("");

    const name = empName.employee_name;
    const checkout_comments = comments;

    const record = await getLastPendingCheckIn(empId);
    if (!record?.pending) {
      setMessage("No pending check-in found for checkout.");
      setOpen(true);
      return;
    }

    // -----------------------------
    // 1️⃣ Resolve checkout time
    // -----------------------------
    let checkoutTimeToUse = checkOutTime;

    if (!checkoutTimeToUse.hour || !checkoutTimeToUse.min) {
      const nowIST = toIST();
      let h = nowIST.getHours();
      const m = nowIST.getMinutes();
      const meridian = h >= 12 ? "PM" : "AM";
      h = h % 12 || 12;

      checkoutTimeToUse = {
        hour: String(h).padStart(2, "0"),
        min: String(m).padStart(2, "0"),
        meridian,
      };

      setCheckOutTime(checkoutTimeToUse);
    }

    const { attendance_id, attendance_date, checkin_time } = record;

    // -----------------------------
    // 2️⃣ Build Date Objects (NO AUTO SHIFT)
    // -----------------------------
    const checkout_time = getISTTimestamp(checkOutDate, checkoutTimeToUse);

    const checkinDateObj = createISTDateFromString(checkin_time);
    const checkoutDateObj = createISTDateFromString(checkout_time);
    const nowIST = toIST();

    // -----------------------------
    // 3️⃣ Validation Rules
    // -----------------------------

    // RULE A — Same day → checkout must be AFTER check-in
    if (checkOutDate === attendance_date) {
      if (checkoutDateObj <= checkinDateObj) {
        setValidationError(
          "Check-out time must be after check-in time for the same day."
        );
        return;
      }
    }

    // RULE B — Allow ONLY next day for midnight crossing
    const checkinDay = new Date(attendance_date);
    const checkoutDay = new Date(checkOutDate);

    checkinDay.setHours(0, 0, 0, 0);
    checkoutDay.setHours(0, 0, 0, 0);

    const diffDays =
      (checkoutDay.getTime() - checkinDay.getTime()) / (1000 * 60 * 60 * 24);

    if (diffDays < 0 || diffDays > 1) {
      setValidationError(
        "Checkout date must be the same day or the next day after check-in."
      );
      return;
    }

    // RULE C — No future checkout
    if (checkoutDateObj > nowIST) {
      setValidationError("Check-out time cannot be in the future.");
      return;
    }

    // RULE D — Max 24 hours
    const workingHours = (checkoutDateObj - checkinDateObj) / (1000 * 60 * 60);

    if (workingHours <= 0) {
      setValidationError("Invalid checkout time.");
      return;
    }

    if (workingHours > 24) {
      setValidationError(
        "Working hours cannot exceed 24 hours. Please verify your dates."
      );
      return;
    }

    // -----------------------------
    // 4️⃣ Submit Checkout
    // -----------------------------
    try {
      const response = await fetch(`${BASE_URL}/api/checkin/out`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          empId,
          boId,
          name,
          attendance_id,
          attendance_date,
          checkout_time,
          checkout_comments,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setIsCheckedIn(false);
        setComments("");
        setDatesCrossMidnight(diffDays === 1); // only user-selected midnight
      }

      setMessage(data.message || "Checkout successful");
      setOpen(true);

      await fetchTodayHistory(page, empId, boId);
      await fetchLastStatus();
    } catch (error) {
      console.error("Check-out error:", error);
      setMessage("Check-out failed. Please try again.");
      setOpen(true);
    }
  };

  const getLastPendingCheckIn = async (empId) => {
    try {
      console.log("Checking for ANY pending check-in (NULL checkout_time):", {
        empId,
        boId,
      });

      // CORRECTED: Don't send currentDate - we want ANY pending checkout regardless of date
      const res = await fetch(
        `${BASE_URL}/api/checkin/last-pending/${empId}/${boId}`,
        {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        }
      );

      const result = await res.json();

      console.log("Pending check-in API response:", result);

      // Additional validation - ensure we have proper data structure
      if (result && typeof result === "object") {
        // If there's an attendance_id but no checkout_time, it's pending
        if (result.attendance_id && !result.checkout_time) {
          result.pending = true;
        }

        console.log("Final pending status:", {
          pending: result.pending,
          attendance_date: result.attendance_date,
          attendance_id: result.attendance_id,
          has_checkout: !!result.checkout_time,
        });
      }

      return result;
    } catch (error) {
      console.error("Error fetching last pending check-in:", error);
      return null;
    }
  };

  const fetchLastStatus = async () => {
    setIsStatusLoading(true);

    console.log("Fetching last status...");

    const record = await getLastPendingCheckIn(empId);
    setPendingRecord(record); // Store the pending record in state
    const today = getTodayDate();

    console.log("Status check:", {
      record,
      today,
      hasPending: record?.pending,
      pendingDate: record?.attendance_date,
    });

    if (record?.pending) {
      const pendingDate = record.attendance_date;

      if (pendingDate === today) {
        // Today's check-in exists but not checked out
        console.log("Today's check-in found, enabling checkout");
        setCheckInDisabled(true);
        setCheckOutDisabled(false);
        setIsCheckedIn(true); // Set isCheckedIn to true for today
        setActionType("checkout");
        setStatusMsg(
          `You are currently checked in for today (${formatToMMDDYYYY(
            today
          )}). Please check out when you're done.`
        );
      } else {
        // Previous day's check-in is pending - BLOCK new check-ins
        const formattedDate = formatToMMDDYYYY(pendingDate);
        const warningMessage = `PENDING CHECKOUT: You forgot to check out on ${formattedDate}. You must complete that checkout before checking in again.`;

        console.log("BLOCKING: Previous day pending checkout detected");

        setMessage(warningMessage);
        setOpen(true);

        // CRITICAL: Disable check-in until pending checkout is resolved
        setCheckInDisabled(true);
        setCheckOutDisabled(false);
        setIsCheckedIn(true); // Keep isCheckedIn true for pending check-in
        setActionType("checkout");
        setStatusMsg(
          `Pending checkout required for ${formattedDate}. Complete checkout to enable new check-ins.`
        );

        console.warn("Pending checkout detected:", {
          pendingDate,
          today,
          message: warningMessage,
        });
      }
    } else {
      // No pending check-in, allow check-in
      console.log("No pending checkout, enabling check-in");
      setCheckInDisabled(false);
      setCheckOutDisabled(true);
      setIsCheckedIn(false); // Ensure isCheckedIn is false when no pending check-in
      setActionType("checkin");
      // Clear any previous status message when there is no pending record
      setStatusMsg("");
    }

    setIsStatusLoading(false);
  };

  const padZero = (num) => (num < 10 ? `0${num}` : `${num}`);
  const parseTimeTo12Hour = (timeStr) => {
    if (!timeStr || typeof timeStr !== "string") {
      return { hour: "", min: "", meridian: "AM" };
    }

    let raw = timeStr.trim().toUpperCase();

    // CASE 1 — format "02:00 PM"
    if (raw.includes("AM") || raw.includes("PM")) {
      const [hm, mer] = raw.split(" ");
      const [h, m] = hm.split(":");

      return {
        hour: h?.padStart(2, "0") || "",
        min: m?.padStart(2, "0") || "",
        meridian: mer || "AM",
      };
    }

    // CASE 2 — format "14:25:00" or "14:25"
    const parts = raw.split(":");
    const hour24 = parseInt(parts[0], 10);

    if (isNaN(hour24)) return { hour: "", min: "", meridian: "AM" };

    const min = parts[1]?.substring(0, 2) || "00";

    const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
    const meridian = hour24 >= 12 ? "PM" : "AM";

    return {
      hour: String(hour12).padStart(2, "0"),
      min: String(min).padStart(2, "0"),
      meridian,
    };
  };

  const fetchTodayHistory = async (pageNum = 1, empId, boId) => {
    try {
      // Reset midnight crossing flag when fetching new data
      setDatesCrossMidnight(false);

      // Send frontend's current date to backend for consistency
      const frontendCurrentDate = getISTDateString(currentDateTime);

      const queryParams = new URLSearchParams({
        page: pageNum,
        limit,
        empId,
        boId,
        currentDate: frontendCurrentDate, // Send frontend date to backend
      });

      console.log("Fetching history with frontend date:", frontendCurrentDate);

      const res = await fetch(
        `${BASE_URL}/api/checkin/today-history?${queryParams}`
      );
      const data = await res.json();

      const rows = data?.data || [];
      // Format attendance_date as MM/DD/YYYY for display & matching
      const formattedRows = rows.map((item) => ({
        ...item,
        attendance_date: formatToMMDDYYYY(item.attendance_date), // e.g. "11/25/2025"
        checkin_time: item.checkin_time || "",
        checkout_time: item.checkout_time || "",
      }));

      setTodayHistory(formattedRows);
      setTotalPages(data.pagination.totalPages);
      setPage(data.pagination.page);

      // Fetch last pending checkin
      const record = await getLastPendingCheckIn(empId);

      if (record?.pending) {
        // Pending record case
        const pendingDateDisplay = formatToMMDDYYYY(record.attendance_date); // For UI display
        setDisplayDate(pendingDateDisplay);

        // For checkout date, use today's date as default, but this will be corrected during checkout if needed
        const todayISO = getISTDateString(); // e.g. "2025-11-25"
        setCheckOutDate(todayISO);

        // Set check-in time from pending record's checkin_time (new format: "2025-12-31 04:40 PM")
        if (record.checkin_time) {
          // Extract time portion from "2025-12-31 04:40 PM" -> "04:40 PM"
          const timeOnly = extractTimeFromTimestamp(record.checkin_time);
          setCheckInTime(parseTimeTo12Hour(timeOnly));
        } else {
          setCheckInTime({ hour: "", min: "", meridian: "AM" });
        }

        // Clear checkout time for pending record (user to input)
        setCheckOutTime({ hour: "", min: "", meridian: "AM" });
      } else {
        // No pending record: show today's record or empty if none
        const todayISO = getISTDateString(); // "YYYY-MM-DD"
        const todayDisplay = formatToMMDDYYYY(todayISO); // "MM/DD/YYYY"
        setDisplayDate(todayDisplay);

        // Find today's record in formatted rows
        const latestEntry = formattedRows.find(
          (item) => item.attendance_date === todayDisplay
        );

        if (latestEntry) {
          // Set check-in time (new format: "2025-12-31 04:40 PM")
          if (latestEntry.checkin_time) {
            const timeOnly = extractTimeFromTimestamp(latestEntry.checkin_time);
            setCheckInTime(parseTimeTo12Hour(timeOnly));
          } else {
            setCheckInTime({ hour: "", min: "", meridian: "AM" });
          }

          // Set check-out time and date (new format: "2026-01-01 01:00 AM")
          if (latestEntry.checkout_time) {
            const timeOnly = extractTimeFromTimestamp(
              latestEntry.checkout_time
            );
            setCheckOutTime(parseTimeTo12Hour(timeOnly));

            // IMPORTANT: Extract the actual checkout date from the checkout_time
            const checkoutDateFromTime = extractDateFromTimestamp(
              latestEntry.checkout_time
            );
            if (checkoutDateFromTime && checkoutDateFromTime !== "—") {
              // Convert MM/DD/YYYY back to YYYY-MM-DD for the date input
              const [month, day, year] = checkoutDateFromTime.split("/");
              const checkoutDateISO = `${year}-${month.padStart(
                2,
                "0"
              )}-${day.padStart(2, "0")}`;
              setCheckOutDate(checkoutDateISO);

              // Check if dates are different for highlighting
              const checkinDateFromTime = extractDateFromTimestamp(
                latestEntry.checkin_time
              );
              if (checkinDateFromTime !== checkoutDateFromTime) {
                setDatesCrossMidnight(true);
              }
            } else {
              setCheckOutDate(todayISO);
            }
          } else {
            setCheckOutTime({ hour: "", min: "", meridian: "AM" });
            setCheckOutDate(todayISO);
          }
        } else {
          // No record for today
          setCheckInTime({ hour: "", min: "", meridian: "AM" });
          setCheckOutTime({ hour: "", min: "", meridian: "AM" });
          setCheckOutDate(todayISO);
        }
      }
    } catch (err) {
      console.error("Error fetching today history:", err);
    }
  };

  const formatDuration = (interval) => {
    if (!interval || typeof interval !== "object") return "-";

    const h = interval.hours || 0;
    const m = interval.minutes || 0;
    const s = interval.seconds || 0;

    // Handle negative durations (show as error)
    if (h < 0 || m < 0 || s < 0) {
      return "Invalid";
    }

    if (h === 0 && m === 0 && s === 0) return "0m";

    const parts = [];
    if (h > 0) parts.push(`${h}h`);
    if (m > 0) parts.push(`${m}m`);
    if (h === 0 && m === 0 && s > 0) parts.push(`${s}s`);

    return parts.join(" ");
  };

  // Helper function to extract date from timestamp
  const extractDateFromTimestamp = (timestamp) => {
    if (!timestamp) return "—";
    try {
      const [datePart] = timestamp.split(" ");
      return formatDate(datePart);
    } catch (error) {
      return "—";
    }
  };

  // Helper function to extract time from timestamp
  const extractTimeFromTimestamp = (timestamp) => {
    if (!timestamp) return "—";
    try {
      // Handle format like "2025-12-31 04:40 PM" -> extract "04:40 PM"
      const parts = timestamp.split(" ");
      if (parts.length >= 3) {
        return `${parts[1]} ${parts[2]}`; // "04:40 PM"
      } else if (parts.length === 2) {
        return parts[1]; // Just time part
      }
      return timestamp;
    } catch (error) {
      return timestamp;
    }
  };

  const fetchHistoryByDate = async (date, empId, userRole, empName = "") => {
    try {
      // Use frontend's current date if no specific date provided
      const searchDate = date || getISTDateString(currentDateTime);

      const queryParams = new URLSearchParams({
        date: searchDate,
        page,
        limit,
        currentDate: getISTDateString(currentDateTime), // Send frontend current date
      });

      // If user is NOT admin, filter by their empId
      if (userRole !== "Admin") {
        queryParams.append("empId", empId);
      } else if (empName) {
        queryParams.append("empName", empName); // Admin can search by employee name
      }

      console.log(
        "Fetching history by date with frontend current date:",
        getISTDateString(currentDateTime)
      );

      const res = await fetch(
        `${BASE_URL}/api/checkin/history?${queryParams.toString()}`
      );
      const data = await res.json();
      setHistoryData(data.data);
      console.log("History Data:", data.data);
      setTotalPages(data.pagination.totalPages);
      return data; // return fetched data so callers can act on it
    } catch (err) {
      console.error("Error fetching history:", err);
      return null;
    }
  };

  // Debounce the fetch to avoid firing on every keystroke
  const debouncedFetchHistory = debounce((date, empId, userRole, empName) => {
    fetchHistoryByDate(date, empId, userRole, empName);
  }, 500); // 500ms delay

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Page Title */}
      <Typography
        variant="h4"
        fontWeight="700"
        sx={{
          mb: 3,
          textAlign: "center",
          background: "linear-gradient(90deg, #336faaff, #42a5f5)",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
        }}
      >
        Attendance Tracker
      </Typography>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "flex-start",
          gap: 4,
          width: "100%",
          flexWrap: "wrap",
        }}
      >
        {/* Check-In/Out Card */}
        <ThemeProvider theme={FormThemes}>
          <CssBaseline />
          <Box sx={{ flex: 1, minWidth: 500, maxWidth: 700 }}>
            <Paper
              elevation={4}
              sx={{
                p: 1,
                borderRadius: 3,
                backgroundColor: "#fafafa",
                textAlign: "center",
              }}
            >
              <Box
                sx={{
                  position: "relative",
                  mb: 3,
                  pb: 1,
                  borderBottom: "1px solid #e0e0e0",
                }}
              >
                {/* Centered Title */}
                <Typography
                  variant="h6"
                  fontWeight="600"
                  sx={{
                    color: "#336faaff",
                    textAlign: "center",
                  }}
                >
                  Check-In / Check-Out
                </Typography>

                {/* Icon on the Right */}
                <Tooltip title="View Attendance History">
                  <IconButton
                    color="primary"
                    onClick={() => {
                      setHistoryOpen(true);
                      fetchHistoryByDate(searchDate, empId, userRole);
                    }}
                    sx={{
                      position: "absolute",
                      right: 40,
                      top: "50%",
                      transform: "translateY(-50%)",
                    }}
                  >
                    <HistoryIcon />
                  </IconButton>
                </Tooltip>

                {/* Logout Button */}
                {/* <Tooltip title="Logout">
                  <IconButton
                    color="secondary"
                    onClick={handleLogout}
                    sx={{
                      position: "absolute",
                      right: 0,
                      top: "50%",
                      transform: "translateY(-50%)",
                    }}
                  >
                    <CloseIcon />
                  </IconButton>
                </Tooltip> */}
              </Box>

              {/* Emergency Debug Section - Remove after fixing */}
              {/* <Box sx={{ mb: 2, p: 2, border: "2px solid #ff9800", borderRadius: 2, backgroundColor: "#fff3e0" }}>
                <Typography variant="h6" color="warning.main" sx={{ mb: 1 }}>
                  🔧 DEBUG MODE - Remove After Fixing
                </Typography>
                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                  <Button
                    variant="outlined"
                    color="warning"
                    size="small"
                    onClick={async () => {
                      console.log("🔍 MANUAL DEBUG: Checking pending status...");
                      const result = await getLastPendingCheckIn(empId);
                      console.log("🔍 MANUAL DEBUG RESULT:", result);
                      alert(`Pending Check Result: ${JSON.stringify(result, null, 2)}`);
                    }}
                  >
                    Check Pending Status
                  </Button>
                  <Button
                    variant="outlined"
                    color="warning"
                    size="small"
                    onClick={async () => {
                      console.log("🔍 MANUAL DEBUG: Fetching history...");
                      const response = await fetch(
                        `${BASE_URL}/api/checkin/today-history?empId=${empId}&boId=${boId}&currentDate=${getISTDateString(currentDateTime)}`
                      );
                      const data = await response.json();
                      console.log("🔍 MANUAL DEBUG HISTORY:", data);
                      alert(`History Data: ${JSON.stringify(data.data, null, 2)}`);
                    }}
                  >
                    Check History
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    onClick={() => {
                      console.log("🔧 MANUAL OVERRIDE: Forcing checkout mode");
                      setCheckInDisabled(true);
                      setCheckOutDisabled(false);
                      setIsCheckedIn(true);
                      setActionType("checkout");
                      setStatusMsg("⚠️ MANUAL OVERRIDE: Forced into checkout mode");
                    }}
                  >
                    Force Checkout Mode
                  </Button>
                </Box>
              </Box> */}

              {statusMsg && (
                <Alert
                  severity={
                    statusMsg.includes("PENDING CHECKOUT") ? "warning" : "info"
                  }
                  sx={{
                    mb: 3,
                    borderRadius: 2,
                    fontSize: "0.9rem",
                    "& .MuiAlert-message": {
                      fontWeight: statusMsg.includes("PENDING CHECKOUT")
                        ? "600"
                        : "normal",
                    },
                  }}
                >
                  {statusMsg}
                </Alert>
              )}
              {/* Date & Employee Info */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  mb: 3,
                  gap: 1,
                }}
              >
                {/* First line: Employee */}
                <Box sx={{ display: "flex", justifyContent: "center", gap: 2 }}>
                  <Chip
                    label={`Employee: ${empName.emp_id} - ${empName.employee_name}`}
                    color="primary"
                    variant="outlined"
                    sx={{
                      width: "auto", // let it grow based on content
                      maxWidth: "100%", // optional: prevents overflowing parent container
                      whiteSpace: "normal", // allow text wrapping (optional)
                    }}
                  />
                </Box>

                {/* Second line: Date & Time */}
                <Box sx={{ display: "flex", justifyContent: "center", gap: 2 }}>
                  <Chip label={`Today's Date: ${currentDate}`} color="info" />
                  <Chip
                    label={`Current Time: ${currentTime} IST`}
                    color="info"
                  />
                  {/* <Chip 
                    label="🔒 Time Protected" 
                    color="success" 
                    // size="small"
                    // sx={{ fontSize: "0.7rem" }}
                    title="System monitors for time manipulation to ensure attendance integrity"
                  /> */}
                </Box>
              </Box>
              <Box sx={{ mb: 1, marginLeft: 8 }}>
                <Grid container spacing={1} alignItems="center">
                  <Grid item xs={2}>
                    <Typography
                      variant="subtitle2"
                      sx={{ color: "#555", textAlign: "left", pr: 1.5 }}
                    >
                      Check-In Date/Time
                    </Typography>
                  </Grid>
                  <Grid item xs={2}>
                    <TextField
                      label="Date"
                      type="date"
                      value={(() => {
                        // Convert displayDate from MM/DD/YYYY to YYYY-MM-DD for date input
                        if (displayDate && displayDate.includes("/")) {
                          const [month, day, year] = displayDate.split("/");
                          return `${year}-${month.padStart(
                            2,
                            "0"
                          )}-${day.padStart(2, "0")}`;
                        }
                        return getISTDateString(); // fallback to today
                      })()}
                      onChange={async (e) => {
                        const selectedDate = e.target.value; // YYYY-MM-DD format
                        const formattedDate = formatToMMDDYYYY(selectedDate); // Convert to MM/DD/YYYY
                        setDisplayDate(formattedDate);

                        // Update checkout date to match check-in date initially
                        setCheckOutDate(selectedDate);

                        // Clear validation errors when date changes
                        setValidationError("");

                        // Reset midnight crossing flag when date changes
                        setDatesCrossMidnight(false);

                        // Fetch record(s) for this date and update card fields
                        try {
                          const data = await fetchHistoryByDate(
                            selectedDate,
                            empId,
                            userRole
                          );

                          const rows = data?.data || [];

                          // For non-admin, API should return only this employee's rows.
                          const record =
                            rows.find((r) => r.emp_id === empId) || rows[0];

                          if (record) {
                            if (record.checkin_time) {
                              const timeOnly = extractTimeFromTimestamp(
                                record.checkin_time
                              );
                              setCheckInTime(parseTimeTo12Hour(timeOnly));
                            } else {
                              setCheckInTime({
                                hour: "",
                                min: "",
                                meridian: "AM",
                              });
                            }

                            if (record.checkout_time) {
                              const timeOnly = extractTimeFromTimestamp(
                                record.checkout_time
                              );
                              setCheckOutTime(parseTimeTo12Hour(timeOnly));

                              const checkoutDateFromTime =
                                extractDateFromTimestamp(record.checkout_time);
                              if (
                                checkoutDateFromTime &&
                                checkoutDateFromTime !== "—"
                              ) {
                                const [month, day, year] =
                                  checkoutDateFromTime.split("/");
                                const checkoutDateISO = `${year}-${month.padStart(
                                  2,
                                  "0"
                                )}-${day.padStart(2, "0")}`;
                                setCheckOutDate(checkoutDateISO);

                                const checkinDateFromTime =
                                  extractDateFromTimestamp(record.checkin_time);
                                if (
                                  checkinDateFromTime !== checkoutDateFromTime
                                ) {
                                  setDatesCrossMidnight(true);
                                }
                              } else {
                                setCheckOutDate(selectedDate);
                              }
                            } else {
                              setCheckOutTime({
                                hour: "",
                                min: "",
                                meridian: "AM",
                              });
                              setCheckOutDate(selectedDate);
                            }
                          } else {
                            setCheckInTime({
                              hour: "",
                              min: "",
                              meridian: "AM",
                            });
                            setCheckOutTime({
                              hour: "",
                              min: "",
                              meridian: "AM",
                            });
                            setCheckOutDate(selectedDate);
                          }
                        } catch (err) {
                          console.error(
                            "Error loading record for selected date:",
                            err
                          );
                        }
                      }}
                      disabled={isCheckedIn} // disable when already checked in
                      InputLabelProps={{ shrink: true }}
                      inputProps={{
                        min: getYesterdayDate(), // Allow yesterday
                        max: getTodayDate(), // Allow up to today
                      }}
                      size="small"
                      sx={{ width: 140 }}
                    />
                  </Grid>
                  <Grid item xs={2}>
                    <TextField
                      select
                      label="Hour"
                      size="small"
                      value={checkInTime.hour}
                      onChange={(e) =>
                        setCheckInTime({ ...checkInTime, hour: e.target.value })
                      }
                      disabled={isCheckedIn} // disables when already checked in
                      sx={{ width: 80 }}
                      SelectProps={{
                        MenuProps: {
                          PaperProps: {
                            sx: {
                              maxHeight: 200, // dropdown height / scroll
                              "& .MuiMenuItem-root": {
                                fontSize: "1rem", // font size of items
                                minHeight: 36, // height per item
                              },
                            },
                          },
                        },
                      }}
                    >
                      {hours.map((h) => (
                        <MenuItem key={h} value={h}>
                          {h}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={2}>
                    <TextField
                      select
                      label="Min"
                      size="small"
                      value={checkInTime.min}
                      onChange={(e) =>
                        setCheckInTime({ ...checkInTime, min: e.target.value })
                      }
                      disabled={isCheckedIn} // disables when already checked in
                      sx={{ width: 80 }}
                      SelectProps={{
                        MenuProps: {
                          PaperProps: {
                            sx: {
                              maxHeight: 200, // dropdown height / scroll
                              "& .MuiMenuItem-root": {
                                fontSize: "1rem", // font size of items
                                minHeight: 36, // height per item
                              },
                            },
                          },
                        },
                      }}
                    >
                      {minutes.map((m) => (
                        <MenuItem key={m} value={m}>
                          {m}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={2}>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      value={checkInTime.meridian}
                      onChange={(e) =>
                        setCheckInTime({
                          ...checkInTime,
                          meridian: e.target.value,
                        })
                      }
                      disabled={isCheckedIn} // disables when already checked in
                      sx={{ width: 80 }}
                    >
                      {ampm.map((a) => (
                        <MenuItem key={a} value={a}>
                          {a}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  {/* Checkout Section */}
                  <Grid item xs={2}>
                    <Typography
                      variant="subtitle2"
                      sx={{ color: "#555", textAlign: "left" }}
                    >
                      Check-Out Date/Time
                    </Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <TextField
                      label="Date"
                      type="date"
                      value={checkOutDate}
                      disabled={checkOutDisabled} // disables when not checked in
                      onChange={(e) => {
                        const selectedDate = e.target.value;
                        setCheckOutDate(selectedDate);

                        // Clear any previous validation errors when date changes
                        setValidationError("");
                        setDatesCrossMidnight(false); // Reset midnight crossing flag

                        // Optional: Show warning if date is more than 1 day from check-in
                        if (pendingRecord?.pending) {
                          const checkinDate = new Date(
                            pendingRecord.attendance_date
                          );
                          const selectedDateObj = new Date(selectedDate);
                          const daysDiff = Math.abs(
                            (selectedDateObj - checkinDate) /
                              (1000 * 60 * 60 * 24)
                          );

                          if (daysDiff > 1) {
                            setValidationError(
                              "Warning: Checkout date is more than 1 day from check-in date. Please verify this is correct."
                            );
                          }
                        }
                      }}
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      inputProps={{
                        min: (() => {
                          // Allow checkout from check-in date
                          if (displayDate && displayDate.includes("/")) {
                            const [month, day, year] = displayDate.split("/");
                            return `${year}-${month.padStart(
                              2,
                              "0"
                            )}-${day.padStart(2, "0")}`;
                          }
                          return getYesterdayDate();
                        })(),
                        max: (() => {
                          // Allow checkout up to the day after check-in (for midnight crossing)
                          if (displayDate && displayDate.includes("/")) {
                            const [month, day, year] = displayDate.split("/");
                            const checkinDate = new Date(year, month - 1, day);
                            const nextDay = new Date(checkinDate);
                            nextDay.setDate(nextDay.getDate() + 1);
                            return getISTDateString(nextDay);
                          }
                          return getTodayDate();
                        })(),
                      }}
                      size="small"
                      sx={{
                        width: 140,
                        "& .MuiOutlinedInput-root": {
                          backgroundColor: datesCrossMidnight
                            ? "#e8f5e8"
                            : "inherit",
                          "& fieldset": {
                            borderColor: datesCrossMidnight
                              ? "#4caf50"
                              : "inherit",
                          },
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={2}>
                    <TextField
                      select
                      label="Hour"
                      size="small"
                      value={checkOutTime.hour}
                      disabled={checkOutDisabled} // disables when not checked in
                      onChange={(e) =>
                        setCheckOutTime({
                          ...checkOutTime,
                          hour: e.target.value,
                        })
                      }
                      sx={{ width: 80 }}
                      SelectProps={{
                        MenuProps: {
                          PaperProps: {
                            sx: {
                              maxHeight: 200, // dropdown height / scroll
                              "& .MuiMenuItem-root": {
                                fontSize: "1rem", // font size of items
                                minHeight: 36, // height per item
                              },
                            },
                          },
                        },
                      }}
                    >
                      {hours.map((h) => (
                        <MenuItem key={h} value={h}>
                          {h}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={2}>
                    <TextField
                      select
                      label="Min"
                      size="small"
                      value={checkOutTime.min}
                      disabled={checkOutDisabled} // disables when not checked in
                      onChange={(e) =>
                        setCheckOutTime({
                          ...checkOutTime,
                          min: e.target.value,
                        })
                      }
                      sx={{ width: 80 }}
                      SelectProps={{
                        MenuProps: {
                          PaperProps: {
                            sx: {
                              maxHeight: 200, // dropdown height / scroll
                              "& .MuiMenuItem-root": {
                                fontSize: "1rem", // font size of items
                                minHeight: 36, // height per item
                              },
                            },
                          },
                        },
                      }}
                    >
                      {minutes.map((m) => (
                        <MenuItem key={m} value={m}>
                          {m}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={2}>
                    <TextField
                      select
                      size="small"
                      value={checkOutTime.meridian}
                      disabled={checkOutDisabled}
                      onChange={(e) =>
                        setCheckOutTime({
                          ...checkOutTime,
                          meridian: e.target.value,
                        })
                      }
                      sx={{ width: 80 }}
                    >
                      {ampm.map((a) => (
                        <MenuItem key={a} value={a}>
                          {a}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  {/*  ADD THIS BLOCK BELOW TIME SELECT BOX */}
                  {dateError && (
                    <Grid item xs={12}>
                      <Typography color="error" variant="body2" sx={{ mt: 2 }}>
                        {dateError}
                      </Typography>
                    </Grid>
                  )}

                  {/* Comments */}
                  <Grid item xs={2}>
                    <TextField
                      label="Comments (optional)"
                      value={comments}
                      onChange={(e) => setComments(e.target.value)}
                      multiline
                      size="small"
                      sx={{
                        marginLeft: 18,
                        width: 405, // same width as your Check-In / Check-Out grid
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>
              {/* Midnight crossing indicator */}
              {datesCrossMidnight && (
                <Grid item xs={12}>
                  <Box
                    sx={{
                      width: "100%",
                      display: "flex",
                      justifyContent: "center",
                      mt: 1,
                    }}
                  >
                    <Typography
                      color="success.main"
                      variant="body2"
                      sx={{
                        fontStyle: "italic",
                        textAlign: "center",
                      }}
                    >
                      Checkout date automatically adjusted for midnight crossing
                    </Typography>
                  </Box>
                </Grid>
              )}

              {/* Buttons */}
              <Box
                display="flex"
                justifyContent="center"
                gap={2}
                sx={{ mt: 1 }}
              >
                <Button
                  variant={actionType === "checkin" ? "contained" : "outlined"}
                  color="primary"
                  onClick={handleCheckIn}
                  disabled={checkInDisabled}
                  sx={{
                    textTransform: "none",
                    px: 3,
                    borderRadius: 2,
                    "&:hover": { backgroundColor: "#1565c0" },
                    "&:disabled": {
                      backgroundColor:
                        checkInDisabled &&
                        pendingRecord?.pending &&
                        pendingRecord.attendance_date !== getTodayDate()
                          ? "#ffebee"
                          : "inherit",
                      color:
                        checkInDisabled &&
                        pendingRecord?.pending &&
                        pendingRecord.attendance_date !== getTodayDate()
                          ? "#d32f2f"
                          : "inherit",
                      border:
                        checkInDisabled &&
                        pendingRecord?.pending &&
                        pendingRecord.attendance_date !== getTodayDate()
                          ? "1px solid #f44336"
                          : "inherit",
                    },
                  }}
                  title={
                    checkInDisabled &&
                    pendingRecord?.pending &&
                    pendingRecord.attendance_date !== getTodayDate()
                      ? `Blocked: Complete checkout for ${formatToMMDDYYYY(
                          pendingRecord.attendance_date
                        )} first`
                      : ""
                  }
                >
                  {checkInDisabled &&
                  pendingRecord?.pending &&
                  pendingRecord.attendance_date !== getTodayDate()
                    ? "⚠️ Check-In Blocked"
                    : "Check-In"}
                </Button>
                <Button
                  variant={actionType === "checkout" ? "contained" : "outlined"}
                  color="primary"
                  onClick={handleCheckOut}
                  disabled={checkOutDisabled}
                  sx={{
                    textTransform: "none",
                    px: 3,
                    borderRadius: 2,
                    "&:hover": { backgroundColor: "#1565c0" },
                  }}
                >
                  Check-Out
                </Button>
              </Box>
              {validationError && (
                <Typography color="error" variant="body2" sx={{ mt: 2 }}>
                  {validationError}
                </Typography>
              )}
            </Paper>
          </Box>
        </ThemeProvider>

        {/* Today's History Table */}
        {/* <ThemeProvider theme={Table_theme}>
          <Box sx={{ flex: 1, minWidth: 500, maxWidth: 1200, mt: 3 }}>
            <Paper
              elevation={4}
              sx={{
                p: 3,
                borderRadius: 3,
                backgroundColor: "#fafafa",
              }}
            >
              <Typography
                variant="h6"
                fontWeight="600"
                sx={{
                  color: "#336faaff",
                  textAlign: "center",
                  mb: 3,
                  pb: 1,
                  borderBottom: "1px solid #e0e0e0",
                }}
              >
                Today's Attendance Records
              </Typography>

              <TableContainer
                component={Paper}
                elevation={2}
                sx={{
                  borderRadius: 2,
                  overflowX: "auto",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                }}
              >
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      {[
                        "Attendance Date",
                        "Check-In Date",
                        "Check-In Time", 
                        "Check-Out Date",
                        "Check-Out Time",
                        "Working Hours",
                        "Comments",
                      ].map((head, i) => (
                        <TableCell
                          key={i}
                          align="left"
                          sx={{
                            whiteSpace: "nowrap",
                            fontSize: "0.9rem",
                            fontWeight: "600",
                            backgroundColor: "#f5f5f5",
                            minWidth: 120,
                          }}
                        >
                          {head}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {todayHistory.length > 0 ? (
                      todayHistory.map((row, idx) => (
                        <TableRow
                          key={idx}
                          sx={{
                            "&:nth-of-type(odd)": { backgroundColor: "#fafafa" },
                            "&:hover": { backgroundColor: "#f0f8ff" },
                            transition: "background-color 0.2s ease",
                          }}
                        >
                          {[
                            formatDate(row.attendance_date),
                            extractDateFromTimestamp(row.checkin_time),
                            extractTimeFromTimestamp(row.checkin_time),
                            extractDateFromTimestamp(row.checkout_time),
                            extractTimeFromTimestamp(row.checkout_time),
                            formatDuration(row.total_hours),
                            row.checkin_comments || row.checkout_comments || "—",
                          ].map((cell, i) => (
                            <TableCell
                              key={i}
                              align="left"
                              sx={{
                                whiteSpace: "nowrap",
                                fontSize: "0.85rem",
                                minWidth: 120,
                                // Highlight different dates
                                backgroundColor: i === 1 || i === 3 ? (
                                  cell !== formatDate(row.attendance_date) ? "#e8f5e8" : "inherit"
                                ) : "inherit",
                              }}
                            >
                              {cell}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell
                          colSpan={7}
                          align="center"
                          sx={{ fontSize: "0.9rem", py: 3 }}
                        >
                          No attendance records for today
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Box>
        </ThemeProvider> */}

        <Dialog
          open={historyOpen}
          onClose={handleHistoryClose}
          fullWidth
          maxWidth="lg"
        >
          <DialogTitle>
            Attendance History
            <IconButton
              aria-label="close"
              onClick={handleHistoryClose}
              sx={{ position: "absolute", right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <DialogContent dividers>
            {/* Search Date Field */}
            <Box display="flex" gap={2} mb={2}>
              <TextField
                label="Search Date"
                type="date"
                value={searchDate}
                onChange={(e) => setSearchDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
              {userRole === "Admin" && (
                <TextField
                  label="Employee Name"
                  type="text"
                  value={searchEmpName}
                  size="small"
                  onChange={(e) => {
                    setSearchEmpName(e.target.value);
                    debouncedFetchHistory(
                      searchDate,
                      null,
                      userRole,
                      e.target.value
                    );
                  }}
                  handleClose
                />
              )}
              <Button
                variant="contained"
                onClick={() =>
                  fetchHistoryByDate(searchDate, empId, userRole, searchEmpName)
                }
              >
                Search
              </Button>
            </Box>

            {/* Attendance Table */}
            <TableContainer
              component={Paper}
              elevation={3}
              sx={{
                borderRadius: 3,
                overflowX: "auto", // horizontal scroll
                boxShadow: "0 4px 10px rgba(0,0,0,0.05)",
                maxHeight: "70vh", // optional vertical scroll
              }}
            >
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    {[
                      "Attendance Date",
                      "Employee ID",
                      "Employee Name",
                      "Check-In Date",
                      "Check-In Time",
                      "Check-Out Date",
                      "Check-Out Time",
                      "Working Hours",
                      "Check In Comments",
                      "Check Out Comments",
                    ].map((head, i) => (
                      <TableCell
                        key={i}
                        align="left"
                        sx={{
                          whiteSpace: "nowrap", // prevent wrapping
                          fontSize: "0.8rem",
                          minWidth: 120, // optional: ensure enough width
                        }}
                      >
                        {head}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {historyData.length > 0 ? (
                    historyData.map((row, idx) => (
                      <TableRow
                        key={idx}
                        sx={{
                          "&:nth-of-type(odd)": { backgroundColor: "#fafafa" },
                          "&:hover": { backgroundColor: "#f0f8ff" },
                          transition: "background-color 0.2s ease",
                        }}
                      >
                        {[
                          formatDate(row.attendance_date),
                          row.emp_id,
                          row.employee_name,
                          extractDateFromTimestamp(row.checkin_time),
                          extractTimeFromTimestamp(row.checkin_time),
                          extractDateFromTimestamp(row.checkout_time),
                          extractTimeFromTimestamp(row.checkout_time),
                          formatDuration(row.total_hours),
                          row.checkin_comments || "—",
                          row.checkout_comments || "—",
                        ].map((cell, i) => (
                          <TableCell
                            key={i}
                            align="left"
                            sx={{
                              whiteSpace: "nowrap",
                              fontSize: "0.8rem",
                              minWidth: 120,
                            }}
                          >
                            {cell}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell
                        colSpan={10}
                        align="center"
                        sx={{ fontSize: "0.8rem" }}
                      >
                        No records found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {/* Pagination */}
            <Box display="flex" justifyContent="center" mt={2}>
              <Pagination
                count={Math.ceil(totalPages)}
                page={page + 1}
                onChange={(_, newPage) => {
                  setPage(newPage - 1);
                  fetchHistoryByDate(searchDate, empId, userRole);
                }}
                color="primary"
              />
            </Box>
          </DialogContent>

          <DialogActions>
            <Button onClick={handleHistoryClose}>Close</Button>
          </DialogActions>
        </Dialog>
        <Snackbar
          open={open}
          autoHideDuration={4000}
          onClose={() => setOpen(false)}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert
            severity={validationError ? "error" : "info"}
            onClose={() => setOpen(false)}
            sx={{ width: "100%", borderRadius: 2 }}
          >
            {message || validationError || "No message available"}
          </Alert>
        </Snackbar>
      </Box>
    </Container>
  );
};

export default EmployeeAttendance;
